// Carry Roll Zustand store — combines slices, wires persist + memoized recompute.
// No React/DOM imports (enforced by eslint-plugin-boundaries).

import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';
import { createSettingsSlice, type SettingsSlice } from './settingsSlice';
import { createInputSlice, type InputSlice } from './inputSlice';
import { createResultSlice, type ResultSlice } from './resultSlice';
import { createSessionSlice, type SessionSlice } from './sessionSlice';
import { createCompareSlice, type CompareSlice } from './compareSlice';
import { createDemoSlice, type DemoSlice } from './demoSlice';
import { localStorageAdapter, INPUT_KEY, SETTINGS_KEY } from '../persist/localStorage';

export type StoreState = SettingsSlice & InputSlice & ResultSlice & SessionSlice & CompareSlice & DemoSlice;

// Merge slices. ResultSlice's recompute is wired to run on every input/settings change
// via the store's subscribe hook (set up in the React provider, selectors.ts).
export const useStore = create<StoreState>()(
  persist(
    (...a) => ({
      ...createSettingsSlice(...a),
      ...createInputSlice(...a),
      ...createResultSlice(...a),
      ...createSessionSlice(...a),
      ...createCompareSlice(...a),
      ...createDemoSlice(...a),
    }),
    {
      name: 'carry-roll.store',
      storage: createJSONStorage(() => ({
        getItem: (name) => localStorageAdapter.getItem(name as string),
        setItem: (name, value) => {
          try {
            localStorageAdapter.setItem(name as string, value as string);
          } catch {
            useStore.setState({
              persistError: 'Storage full — export and clear old sessions to continue saving.',
            });
          }
        },
        removeItem: (name) => localStorageAdapter.removeItem(name as string),
      })),
      // Persist settings + input only (result is derived).
      partialize: (state) => {
        const s = state as StoreState;
        return {
          distanceUnit: s.distanceUnit,
          speedUnit: s.speedUnit,
          heightUnit: s.heightUnit,
          inputMode: s.inputMode,
          distanceMode: s.distanceMode,
          fidelity: s.fidelity,
          version: s.version,
          showLoadDemo: s.showLoadDemo,
          fairwayWidthM: s.fairwayWidthM,
          theme: s.theme,
          request: s.request,
        };
      },
      merge: (persisted, current) => {
        const p = (persisted ?? {}) as Partial<StoreState>;
        // Migration hook: bump version + migrate when schema changes.
        if (p.version != null && p.version < current.version) {
          // Future migrations go here; currently v1 → v1 (no-op).
        }
        const merged = { ...current, ...p, version: current.version };
        // Recompute result from the restored request.
        merged.recompute(merged.request);
        return merged;
      },
    },
  ),
);

export { SETTINGS_KEY, INPUT_KEY };
